import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Warehouse,
  Plane,
  Building2,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

interface MT1Variant {
  name: string;
  tagline: string;
  Icon: typeof Warehouse;
  href: string;
  environments: string[];
  features: string[];
  badge?: string;
}

const variants: MT1Variant[] = [
  {
    name: "PUDU MT1",
    tagline: "Adaptive sweeping for warehouses, factories, and venues.",
    Icon: Warehouse,
    href: "/mt1",
    environments: ["Warehouses", "Factories", "Sports Venues"],
    features: [
      "AI adaptive cleaning",
      "Edge & spot cleaning",
      "Debris sweeping at scale",
    ],
  },
  {
    name: "PUDU MT1 Vac",
    tagline: "Hurricane-suction vacuuming for carpets and production lines.",
    Icon: Plane,
    href: "/mt1-vac",
    environments: ["Airports", "Production Lines", "Carpeted Facilities"],
    features: [
      "Industrial vacuum power",
      "Dust & lint management",
      "Carpet-rated cleaning",
    ],
    badge: "Most Popular",
  },
  {
    name: "PUDU MT1 Max",
    tagline: "Heavy-duty industrial sweeping for semi-outdoor operations.",
    Icon: Building2,
    href: "/mt1-max",
    environments: ["Parking Garages", "Receiving Areas", "Semi-Outdoor"],
    features: [
      "3D LiDAR navigation",
      "Smart obstacle avoidance",
      "Industrial sweeping power",
    ],
  },
];

const MT1SeriesSection = () => {
  return (
    <section className="py-20">
      <div className="text-center mb-14 max-w-3xl mx-auto px-4">
        <p className="text-xs md:text-sm font-bold tracking-[0.25em] text-primary mb-3">
          PUDU MT1 SERIES
        </p>
        <h2 className="font-orbitron font-bold text-3xl md:text-5xl mb-4 text-foreground leading-tight">
          Industrial Cleaning,{" "}
          <span className="text-primary">Fully Autonomous</span>
        </h2>
        <p className="text-base md:text-lg text-muted-foreground">
          Three purpose-built robots engineered for the hardest commercial environments — sweeping, vacuuming, and scrubbing 24/7.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {variants.map(({ name, tagline, Icon, href, environments, features, badge }) => (
          <Card
            key={name}
            className="group relative overflow-hidden border border-[hsl(var(--line))] glass-effect p-7 hover:border-primary/50 hover:-translate-y-1 transition-all duration-500"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
          >
            {/* Top accent line */}
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-secondary to-transparent opacity-70" />

            {badge && (
              <span className="absolute top-4 right-4 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">
                {badge}
              </span>
            )}

            <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 border border-primary/30 text-primary group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
              <Icon className="w-7 h-7" />
            </div>

            <h3 className="font-orbitron font-bold text-xl md:text-2xl text-foreground mb-2">
              {name}
            </h3>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              {tagline}
            </p>

            <div className="mb-5">
              <p className="text-[10px] font-bold tracking-[0.2em] text-secondary/80 uppercase mb-2">
                Built For
              </p>
              <div className="flex flex-wrap gap-1.5">
                {environments.map((env) => (
                  <span
                    key={env}
                    className="text-xs px-2.5 py-1 rounded-full border border-[hsl(var(--line))] bg-white/[0.03] text-foreground/80"
                  >
                    {env}
                  </span>
                ))}
              </div>
            </div>

            <ul className="space-y-2 mb-6">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-foreground/85">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Button
              asChild
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
            >
              <a href={href} className="inline-flex items-center justify-center gap-2">
                Get a Quote
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>

            {/* Hover glow */}
            <div
              className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "radial-gradient(circle at 30% 0%, hsl(var(--primary) / 0.15), transparent 60%)",
              }}
            />
          </Card>
        ))}
      </div>
    </section>
  );
};

export default MT1SeriesSection;
